/**
 * User Service - Centralized authentication and user management
 * Handles logout, token management, and 401 error handling
 */

import { BASE_URL } from "../config";
import axios from "axios";

// Flag to prevent multiple simultaneous logout calls
let isLoggingOut = false;

// Flag to track if global interceptor is set up
let globalInterceptorSetup = false;

/**
 * Centralized logout function that handles all cleanup and navigation
 * This should be called whenever a 401 Unauthorized error is detected
 */
export const logout = () => {
  // Prevent multiple simultaneous logout calls
  if (isLoggingOut) {
    console.log('[UserService] Logout already in progress, skipping...');
    return;
  }
  
  isLoggingOut = true;
  try {
    console.log('[UserService] Logging out user due to 401 Unauthorized');
    
    // Get the FCM token before clearing session
    const fcmToken = localStorage.getItem('fcmToken');
    const userId = localStorage.getItem('userId');
    
    // Call the window handler for logout which handles FCM token deletion
    if (typeof window.handleLogout === 'function') {
      // Use setTimeout to avoid blocking the logout process
      setTimeout(() => {
        window.handleLogout().catch((error) => {
          console.error('[UserService] Error in window.handleLogout:', error);
        });
      }, 0);
    }
    
    // Dispatch custom event for AndroidLocationBridge to react
    window.dispatchEvent(new Event('userLoggedOut'));
    
    // Clear all localStorage data
    localStorage.removeItem("token");
    localStorage.removeItem("sidebarPermission");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("userId");
    localStorage.removeItem("userType");
    localStorage.removeItem("profileCompletionPercent");
    localStorage.removeItem("profileCompletionOwnerKey");
    localStorage.removeItem("fcmToken");
    sessionStorage.removeItem("fcmToken");
    
    // Navigate to login page
    // Check if we're in a browser environment (not SSR)
    if (typeof window !== 'undefined' && window.location) {
      // Use window.location.href for immediate navigation
      // This ensures navigation happens even if React Router is not available
      window.location.href = '/';
    }
    
    console.log('[UserService] User logged out successfully');
    isLoggingOut = false;
  } catch (error) {
    console.error('[UserService] Error during logout:', error);
    // Still clear storage even if there's an error
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("sidebarPermission");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("userId");
      localStorage.removeItem("userType");
      localStorage.removeItem("profileCompletionPercent");
      localStorage.removeItem("profileCompletionOwnerKey");
      localStorage.removeItem("fcmToken");
      sessionStorage.removeItem("fcmToken");
      
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = '/';
      }
    } catch (clearError) {
      console.error('[UserService] Error clearing storage:', clearError);
    } finally {
      isLoggingOut = false;
    }
  }
};

/**
 * Check if a response is a 401 Unauthorized error
 * @param {Object} error - The error object from axios
 * @returns {boolean} - True if it's a 401 error
 */
export const is401Error = (error) => {
  return error?.response?.status === 401 || error?.status === 401;
};

/**
 * Handle 401 errors - logs out the user
 * @param {Object} error - The error object from axios
 */
export const handle401Error = (error) => {
  if (is401Error(error)) {
    console.warn('[UserService] 401 Unauthorized detected, logging out user');
    logout();
  }
};

/**
 * Setup global axios interceptor for direct axios calls
 * This handles 401 errors for any axios calls made directly (not through client service)
 * Only intercepts requests to our BASE_URL to avoid affecting external API calls
 */
export const setupGlobalAxiosInterceptor = () => {
  // Prevent duplicate interceptors
  if (globalInterceptorSetup) {
    console.log('[UserService] Global axios interceptor already set up');
    return;
  }
  
  // Error interceptor for failed responses
  const globalErrorInterceptor = (error) => {
    // Only handle 401 errors for requests to our BASE_URL
    if (error?.response?.status === 401) {
      const requestUrl = error?.config?.url || error?.request?.responseURL || '';
      const baseUrl = error?.config?.baseURL || '';
      const fullUrl = requestUrl.startsWith('http') ? requestUrl : (baseUrl + requestUrl);
      
      // Only logout if it's a request to our API (not external APIs like LocationIQ)
      if (BASE_URL && fullUrl.includes(BASE_URL)) {
        console.warn('[UserService] 401 Unauthorized in direct axios call to:', fullUrl);
        logout();
      }
    }
    return Promise.reject(error);
  };
  
  // Add interceptor to default axios instance
  axios.interceptors.response.use(
    (response) => response,
    globalErrorInterceptor
  );
  
  globalInterceptorSetup = true;
  console.log('[UserService] Global axios interceptor set up successfully');
};
